/**
 * KAN-862: состояние страницы CRM в адресе: вид (?view=table|board|stats; без
 * параметра узкий экран открывает доску, широкий таблицу) и настройки доски
 * (KAN-883: период активности, lib/crmBoardActivity). KAN-885: исход таблицы ?outcome= в useCrmTablePeriod.
 * KAN-882: вид доски ?board=work|managers|temperature|funnel; ?group= больше не
 * читается, ?outcome= только у таблицы.
 * KAN-865: ?attention=1 - только требующие внимания; ?card=<id> - открытая
 * панель карточки клиента (открытие новой записью истории: "Назад" закрывает).
 * KAN-910: всё в адресе, чтобы ссылкой можно было поделиться: выбранная колонка доски
 * ?column=<ключ> (сбрасывается сменой вида доски), быстрый период активности именем
 * ?activity=today|yesterday|7d|all (setActivityPreset), даты календаря activity_from/activity_to.
 * Умолчания вида (зависит от ширины экрана), вида доски и активности пишутся в адрес
 * (useCrmPageDefaults, useCrmBoardDefaults). Смена вида CRM снимает период-умолчание
 * ушедшего вида (Сегодня таблицы, 30 дней "Лидов по дням"), чтобы новый вид открылся со своим;
 * выбранный вручную период переносится. Адрес - useUrlState.
 */
import { useCallback, useMemo } from 'react';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useUrlDefaults, useUrlState } from '@/hooks/useUrlState';
import { DEFAULT_BOARD_VIEW, parseBoardView, parseCrmView, type CrmView } from '@/lib/crmPipeline';
import type { CrmBoardView } from '@/types/crmPipeline';
import {
  CRM_BOARD_ACTIVITY_LEGACY_PARAMS,
  CRM_BOARD_ACTIVITY_PARAM,
  CRM_BOARD_DEFAULT_ACTIVITY,
  parseBoardActivity,
  withBoardActivity,
} from '@/lib/crmBoardActivity';
import { CRM_STATS_DEFAULT_PERIOD } from '@/lib/crmStats';
import type { CrmRange } from '@/lib/crmTable';
import { CRM_PERIOD_PARAM, CRM_TABLE_DEFAULT_PERIOD } from '@/lib/crmTablePeriod';
import type { PeriodPreset } from '@/lib/periodPresets';

export const NARROW_SCREEN_QUERY = '(max-width: 767px)';

/** KAN-910: параметры адреса страницы CRM и доски. */
export const CRM_VIEW_PARAM = { view: 'view', board: 'board', column: 'column', attention: 'attention', card: 'card' } as const;

/** Период по умолчанию видов с общим ?period= (у доски свой ?activity=). */
const VIEW_DEFAULT_PERIOD: Partial<Record<CrmView, string>> = {
  table: CRM_TABLE_DEFAULT_PERIOD,
  stats: CRM_STATS_DEFAULT_PERIOD,
};

/** Адрес нового вида CRM: период-умолчание другого вида снимается (новый вид запишет свой). */
export function withCrmView(prev: URLSearchParams, view: CrmView): URLSearchParams {
  const next = new URLSearchParams(prev);
  next.set(CRM_VIEW_PARAM.view, view);
  const period = next.get(CRM_PERIOD_PARAM.period);
  const foreignDefault = Object.entries(VIEW_DEFAULT_PERIOD).some(([v, value]) => v !== view && value === period);
  if (period && foreignDefault && VIEW_DEFAULT_PERIOD[view] !== period) next.delete(CRM_PERIOD_PARAM.period);
  return next;
}

export function useCrmBoardView() {
  const { params, update, setParam } = useUrlState();
  const narrow = useMediaQuery(NARROW_SCREEN_QUERY);
  const defaultView: CrmView = narrow ? 'board' : 'table';
  const view: CrmView = parseCrmView(params.get(CRM_VIEW_PARAM.view)) ?? defaultView;
  const boardView = parseBoardView(params.get(CRM_VIEW_PARAM.board));
  // Период пересчитывается только при смене своих параметров (стабильная ссылка для ключей запросов).
  const activityName = params.get(CRM_BOARD_ACTIVITY_PARAM.period);
  const activityFrom = params.get(CRM_BOARD_ACTIVITY_PARAM.from);
  const activityTo = params.get(CRM_BOARD_ACTIVITY_PARAM.to);
  const legacyActive = params.get('active');
  const legacyActiveDays = params.get('active_days');
  const activity = useMemo(() => {
    const own = new URLSearchParams();
    if (activityName) own.set(CRM_BOARD_ACTIVITY_PARAM.period, activityName);
    if (activityFrom) own.set(CRM_BOARD_ACTIVITY_PARAM.from, activityFrom);
    if (activityTo) own.set(CRM_BOARD_ACTIVITY_PARAM.to, activityTo);
    if (legacyActive) own.set('active', legacyActive);
    if (legacyActiveDays) own.set('active_days', legacyActiveDays);
    return parseBoardActivity(own);
  }, [activityName, activityFrom, activityTo, legacyActive, legacyActiveDays]);

  const attentionOnly = params.get(CRM_VIEW_PARAM.attention) === '1';
  const cardId = params.get(CRM_VIEW_PARAM.card) || null;
  const column = params.get(CRM_VIEW_PARAM.column) || null;

  const setView = useCallback((next: CrmView) => update((prev) => withCrmView(prev, next)), [update]);
  const setBoardView = useCallback(
    (next: CrmBoardView) =>
      update((prev) => {
        // Выбранная колонка принадлежит виду доски: смена вида начинает с колонки по умолчанию.
        if (next !== parseBoardView(prev.get(CRM_VIEW_PARAM.board))) prev.delete(CRM_VIEW_PARAM.column);
        prev.set(CRM_VIEW_PARAM.board, next);
      }),
    [update]
  );

  return {
    view,
    defaultView,
    narrow,
    setView,
    boardView,
    setBoardView,
    activity,
    setActivity: useCallback((next: CrmRange | null) => update((prev) => withBoardActivity(prev, next)), [update]),
    setActivityPreset: useCallback(
      (next: PeriodPreset) => update((prev) => withBoardActivity(prev, next)),
      [update]
    ),
    attentionOnly,
    setAttentionOnly: useCallback(
      (next: boolean) => setParam(CRM_VIEW_PARAM.attention, next ? '1' : null),
      [setParam]
    ),
    column,
    setColumn: useCallback((key: string) => setParam(CRM_VIEW_PARAM.column, key), [setParam]),
    cardId,
    openCard: useCallback((clientId: string) => setParam(CRM_VIEW_PARAM.card, clientId, { push: true }), [setParam]),
    closeCard: useCallback(() => setParam(CRM_VIEW_PARAM.card, null), [setParam]),
  };
}

/** KAN-910: вид CRM по умолчанию (по ширине экрана) пишется в адрес страницы /crm. */
export function useCrmPageDefaults(defaultView: CrmView): void {
  useUrlDefaults([{ key: CRM_VIEW_PARAM.view, value: defaultView }]);
}

/** KAN-910: вид доски и период активности по умолчанию пишутся в адрес (страница /crm и /m/board). */
export function useCrmBoardDefaults(): void {
  useUrlDefaults([
    { key: CRM_VIEW_PARAM.board, value: DEFAULT_BOARD_VIEW },
    {
      key: CRM_BOARD_ACTIVITY_PARAM.period,
      value: CRM_BOARD_DEFAULT_ACTIVITY,
      unless: [CRM_BOARD_ACTIVITY_PARAM.from, CRM_BOARD_ACTIVITY_PARAM.to, ...CRM_BOARD_ACTIVITY_LEGACY_PARAMS],
    },
  ]);
}
